import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Label, Modal, Select, useToast } from '@nexply/ui';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import type { MemberFieldDefDTO, MemberFieldType } from '@nexply/api-client';
import { api } from '../lib/api.js';

export const MEMBER_FIELD_TYPE_LABEL: Record<MemberFieldType, string> = {
  TEXT: 'Text',
  LONGTEXT: 'Langer Text',
  NUMBER: 'Zahl',
  DATE: 'Datum',
  SELECT: 'Auswahl',
  CHECKBOX: 'Ja / Nein',
};

/** Die eigenen Felder eines Nutzers — überall dieselbe Query, damit alles sofort mitzieht. */
export function useMemberFields() {
  return useQuery({
    queryKey: ['members', 'fields'],
    queryFn: () => api.members.listFields(),
    staleTime: 5 * 60_000,
  });
}

export function MemberFieldsManager({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useMemberFields();

  const [label, setLabel] = useState('');
  const [type, setType] = useState<MemberFieldType>('TEXT');
  const [optionsRaw, setOptionsRaw] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['members'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.members.createField({
        label: label.trim(),
        type,
        options:
          type === 'SELECT'
            ? optionsRaw.split(',').map((o) => o.trim()).filter(Boolean)
            : undefined,
      }),
    onSuccess: () => {
      setLabel('');
      setOptionsRaw('');
      setType('TEXT');
      setError(null);
      refresh();
    },
    onError: (e: { code?: string }) =>
      setError(
        e.code === 'options_required'
          ? 'Für eine Auswahl brauchst du mindestens eine Option.'
          : e.code === 'too_many_fields'
            ? 'Mehr als 30 eigene Felder gehen nicht.'
            : 'Feld konnte nicht angelegt werden.',
      ),
  });

  const rename = useMutation({
    mutationFn: ({ id, label: l }: { id: string; label: string }) =>
      api.members.updateField(id, { label: l }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.members.removeField(id),
    onSuccess: () => {
      toast.success('Feld gelöscht.');
      refresh();
    },
    onError: () => toast.error('Löschen fehlgeschlagen.'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (label.trim()) create.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Eigene Felder"
      description="Zusatzfelder für deine Mitglieder — nur für dich sichtbar."
      size="lg"
    >
      <div className="space-y-5">
        <form onSubmit={submit} className="space-y-3 rounded-2xl border border-line bg-raise p-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
            <div>
              <Label htmlFor="mf-label">Bezeichnung</Label>
              <Input
                id="mf-label"
                value={label}
                maxLength={60}
                placeholder="z.B. Kennengelernt auf"
                onChange={(e) => setLabel(e.currentTarget.value)}
              />
            </div>
            <div>
              <Label htmlFor="mf-type">Art</Label>
              <Select
                id="mf-type"
                value={type}
                onChange={(e) => setType(e.currentTarget.value as MemberFieldType)}
              >
                {(Object.keys(MEMBER_FIELD_TYPE_LABEL) as MemberFieldType[]).map((t) => (
                  <option key={t} value={t}>
                    {MEMBER_FIELD_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {type === 'SELECT' && (
            <div>
              <Label htmlFor="mf-options">Optionen (kommagetrennt)</Label>
              <Input
                id="mf-options"
                value={optionsRaw}
                placeholder="z.B. Messe, Empfehlung, Online"
                onChange={(e) => setOptionsRaw(e.currentTarget.value)}
              />
            </div>
          )}

          {error && <div className="text-xs text-danger">{error}</div>}

          <Button type="submit" disabled={!label.trim() || create.isPending}>
            <Plus className="h-4 w-4" /> Feld anlegen
          </Button>
        </form>

        {isLoading && <div className="text-sm text-text-muted">Lädt…</div>}

        {data && data.fields.length === 0 && (
          <div className="rounded-2xl border border-dashed border-line py-8 text-center text-sm text-text-dim">
            Noch keine eigenen Felder. Was du hier anlegst, erscheint bei jedem Mitglied.
          </div>
        )}

        {data && data.fields.length > 0 && (
          <div className="space-y-2">
            {data.fields.map((f) => (
              <FieldRow
                key={f.id}
                field={f}
                onRename={(l) => rename.mutate({ id: f.id, label: l })}
                onDelete={() => {
                  if (
                    confirm(
                      `Feld „${f.label}" löschen? Die bei deinen Mitgliedern erfassten Werte dieses Feldes werden mitgelöscht.`,
                    )
                  ) {
                    remove.mutate(f.id);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function FieldRow({
  field,
  onRename,
  onDelete,
}: {
  field: MemberFieldDefDTO;
  onRename: (label: string) => void;
  onDelete: () => void;
}) {
  const [label, setLabel] = useState(field.label);
  const dirty = label.trim() !== field.label && label.trim().length > 0;

  return (
    <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-2 p-2.5">
      <GripVertical className="h-4 w-4 shrink-0 text-text-muted" />
      <Input
        value={label}
        maxLength={60}
        onChange={(e) => setLabel(e.currentTarget.value)}
        onBlur={() => dirty && onRename(label.trim())}
        className="h-9 text-sm"
      />
      <span className="shrink-0 rounded-lg border border-line px-2 py-1 text-[11px] text-text-muted">
        {MEMBER_FIELD_TYPE_LABEL[field.type]}
        {field.type === 'SELECT' && field.options.length > 0 && ` · ${field.options.length}`}
      </span>
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Feld ${field.label} löschen`}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-text-muted hover:bg-danger/10 hover:text-danger transition"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}
